import { Form } from "@client/content/controls";
import { SPACE_BETWEEN_BUTTONS_STYLE } from "@client/content/controls/Form/lib";
import { ModuleConfirmModal } from "@client/content/controls/Modals/Confirm";
import { getRootContainerProps, getRootContainerPropsDep } from "@client/content/lib/helper";
import { ModuleButton, ModuleSecondaryButton } from "@client/content/modules/Button";
import { type RootContainerProps } from "@client/content/types";
import { ShowMoreTextAlert } from "@client/controls/Alert/component";
import Badge from "@client/controls/Badge/component";
import Button from "@client/controls/Button/component";
import { type FormButtonProps } from "@client/controls/Form/component";
import Icon from "@client/controls/Icon/component";
import TextArea from "@client/controls/Inputs/TextArea/component";
import Rate, { RatingSurvey } from "@client/controls/Rate/component";
import { TimelineGroupped, type TimelineItemInfo } from "@client/controls/Timeline/component";
import { getAssetUrl } from "@client/lib/assets";
import date from "@client/lib/date";
import { formatToHtml } from "@client/lib/format";
import { goTo } from "@client/lib/navigation";
import { IconType, MediaType } from "@client/types";
import { type UploadEndpointInfo } from "@common/components/Media/Upload/types";
import { pickUserProfilePictureProps, UserProfilePicture } from "@common/components/ProfilePicture/component";
import { SystemColor } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import React from "react";
import { getTicketStatusBadgeType, mapUserName } from "../lib";
import { type MemberHelpdesk } from "../types";

import "./style.less";

type FeedbackProps = {
  items: MemberHelpdesk.Api.UserTicketFeedback[];
};

const FeedbackContent = (props: FeedbackProps) => {
  return (
    <>
      {props.items.map((x) => (
        <div key={x.ticketFeedbackQuestionId} className="feedback">
          <div>{x.question}</div>
          {!!x.rating && <Rate rate={x.rating ?? 0}></Rate>}
          {x.answer && <div>{x.answer}</div>}
        </div>
      ))}
    </>
  );
};

export type MapProps = RootContainerProps & {
  canLeaveFeedback: boolean;
  detail: MemberHelpdesk.Detail | null;
  error?: string | null;
  isEditMode: boolean;
  status: MemberHelpdesk.Status;
  surveyPageUrl: string;
  uploadApiInfo?: UploadEndpointInfo | null;
  violations?: MemberHelpdesk.Api.GuidelineViolation[] | null;
};

export type ActionProps = {
  onSubmit: (data: MemberHelpdesk.View.NewComment) => void;
  onCloseTicket: () => void;
  onRate: (rating: number, ticketFeedbackQuestionId: string, permalink: string | null) => void;
  onChange?: (name: string, value: any) => void;
  onCloseViolation?: (data: string) => void;
};

export type Props = ActionProps & MapProps;

export default (props: Props) => {
  const i18n = useI18n();

  const [isDislike, setIsDislike] = React.useState<boolean | null>(null);
  const timelineRef = React.useRef<HTMLDivElement>(null);
  const [showConfirmation, setShowConfirmation] = React.useState<boolean>(false);

  React.useEffect(() => {
    timelineRef.current?.scrollTo({ top: timelineRef.current.scrollHeight });
  }, [props.detail?.comments]);

  const ticketDetail = props.detail?.ticketDetail;
  const timelineItems = React.useMemo<TimelineItemInfo[]>(() => {
    const ticketMessageItem: TimelineItemInfo = {
      date: ticketDetail?.createdOnDate ?? "-",
      content: (
        <div className="comment" dangerouslySetInnerHTML={{ __html: formatToHtml(ticketDetail?.message ?? "-") }} />
      ),
      title: mapUserName({
        userFirstName: ticketDetail?.contactFirstName ?? null,
        userLastName: ticketDetail?.contactLastName ?? null
      })!,
      icon: (
        <UserProfilePicture
          email={null}
          firstName={ticketDetail?.contactFirstName}
          lastName={ticketDetail?.contactLastName}
          profilePictureMediaSourceId={ticketDetail?.contactProfilePictureSourceId}
          profilePictureMediaSourceKey={ticketDetail?.contactProfilePictureMediaSourceKey}
          size={32}
        />
      ),
      attachments: props.detail?.attachments
        .filter((x) => !x.ticketCommentId)
        .map((x) => ({
          name: x.mediaName,
          url: getAssetUrl(x.mediaSourceId, MediaType.Image)
        }))
    };

    const comments: TimelineItemInfo[] = (props.detail?.comments ?? []).map((x) => ({
      content: <div className="comment" dangerouslySetInnerHTML={{ __html: formatToHtml(x.comment) }} />,
      date: x.createdOnDate,
      icon: <UserProfilePicture {...pickUserProfilePictureProps(x)} size={"2em"} />,
      id: x.ticketCommentId,
      title: mapUserName({ userFirstName: x.userFirstName, userLastName: x.userLastName }) ?? "-",
      attachments: (props.detail?.attachments ?? [])
        .filter((y) => y.ticketCommentId === x.ticketCommentId)
        .map((y) => ({
          name: y.mediaName,
          url: getAssetUrl(y.mediaSourceId, MediaType.Image)
        }))
    }));

    const feedbacks = props.detail?.userTicketFeedbacks ?? [];
    const feedbackItem: TimelineItemInfo | null = feedbacks.length
      ? {
          date: feedbacks[0].createdOnDate,
          content: <FeedbackContent items={feedbacks} />,
          icon: <Icon iconClassName={IconType.Star} color={SystemColor.Blue} />,
          title: mapUserName({
            userFirstName: ticketDetail!.contactFirstName,
            userLastName: ticketDetail!.contactLastName
          })
        }
      : null;

    return feedbackItem ? [ticketMessageItem, ...comments, feedbackItem] : [ticketMessageItem, ...comments];
  }, [
    props.detail?.ticketDetail,
    props.detail?.comments,
    props.detail?.attachments,
    props.detail?.userTicketFeedbacks
  ]);

  const FormButton = React.useCallback(
    (innerProps: FormButtonProps) => (
      <ModuleButton
        {...getRootContainerProps(props)}
        isDisabled={props.status === "Commenting"}
        onClick={(e) => {
          innerProps.ifValidated(props.onSubmit)(e);
          if (props.onChange) {
            props.onChange("comment", null), props.onChange("mediaIds", []);
          }
        }}
      >
        {i18n.reply}
      </ModuleButton>
    ),
    [...getRootContainerPropsDep(props), props.onSubmit]
  );

  const CloseButton = React.useCallback(
    (innerProps: FormButtonProps) => (
      <ModuleSecondaryButton
        {...getRootContainerProps(props)}
        isDisabled={props.status === "Commenting" || props.isEditMode}
        onClick={() => setShowConfirmation(true)}
      >
        {i18n.closeTicket}
      </ModuleSecondaryButton>
    ),
    [...getRootContainerPropsDep(props)]
  );

  const survey = React.useMemo(() => {
    if (!props.detail?.feedbackQuestions.length) {
      return null;
    }

    const feedback = props.detail!.feedbackQuestions[0];
    const permalink = `${props.surveyPageUrl}?t=${props.detail.ticketDetail.ticketId}&returnUrl=${encodeURIComponent(
      window.location.pathname
    )}`;
    if (feedback.includeRating) {
      return (
        <RatingSurvey
          value={null}
          question={feedback.question}
          onClick={(rating) =>
            props.onRate(
              rating,
              feedback.ticketFeedbackQuestionId!,
              props.detail!.feedbackQuestions.length === 1 && !feedback.includeFreeText ? null : permalink
            )
          }
        />
      );
    }

    if (feedback.includeFreeText) {
      return (
        <div>
          <p>{i18n.takeSurveyRequest}</p>
          <Button
            className="btn-light"
            onClick={() => {
              goTo(permalink);
            }}
          >
            {i18n.takeSurvey}
          </Button>
        </div>
      );
    }

    return null;
  }, [
    props.detail?.feedbackQuestions,
    props.canLeaveFeedback,
    props.detail?.ticketDetail.ticketStatusKey,
    props.surveyPageUrl,
    props.detail?.ticketDetail.ticketId
  ]);

  const yesNoSurvey = React.useMemo(() => {
    return (
      <div className="yes_no_survey">
        {!isDislike ? (
          <>
            <p className="header">{i18n.issueResolvedConfirmation}</p>
            <div>
              <Button
                className="like"
                iconClassName="fal fa-thumbs-up fa-inverse"
                isIconAfter
                onClick={props.onCloseTicket}
              >
                {i18n.yes}
              </Button>
              <Button
                className="dislike"
                iconClassName="fal fa-thumbs-down fa-inverse"
                isIconAfter
                onClick={() => setIsDislike(true)}
              >
                {i18n.no}
              </Button>
            </div>
          </>
        ) : (
          <p>{i18n.describeYourProblem}</p>
        )}
      </div>
    );
  }, [props.onCloseTicket, setIsDislike, isDislike]);

  const violations = React.useMemo(
    () =>
      props.violations ? (
        <>
          {props.violations.map((x) => (
            <ShowMoreTextAlert
              title={x.guideline}
              description={x.description}
              color={SystemColor.Orange}
              onClose={() => props.onCloseViolation && props.onCloseViolation(x.guidelineViolationId)}
            />
          ))}
        </>
      ) : null,
    [props.violations, props.onCloseViolation]
  );

  const fields = React.useMemo(
    () => [
      {
        name: "comment",
        isRequired: true,
        defaultValue: "",
        Component: TextArea,
        componentOpts: { placeholder: i18n.addAReply, rows: 5 }
      }
    ],
    []
  );

  return (
    <div className="wsc_ticket_detail_content">
      <div className="main_block">
        <div className="items_wrap" ref={timelineRef}>
          <TimelineGroupped items={timelineItems} />
        </div>
        {props.detail?.ticketDetail?.ticketStatusKey === "Resolved" && (
          <div className="survey_container">{yesNoSurvey}</div>
        )}
        {props.canLeaveFeedback &&
          !!props.detail?.feedbackQuestions.length &&
          !props.detail?.userTicketFeedbacks.length &&
          props.detail?.ticketDetail.ticketStatusKey === "Closed" && <div className="survey_container">{survey}</div>}
        {props.detail?.ticketDetail.ticketStatusKey === "Closed" ? (
          <div className="close_ticket_container">{i18n.ticketClosed}</div>
        ) : (
          <Form
            topInfo={violations}
            {...getRootContainerProps(props)}
            buttonsStyle={SPACE_BETWEEN_BUTTONS_STYLE}
            fields={fields}
            onSubmit={props.onSubmit}
            Button={FormButton}
            resetOnSubmit
            uploadApiInfo={props.uploadApiInfo}
            extraButtons={CloseButton}
            onChange={props.onChange}
          />
        )}
      </div>

      <div className="info_block">
        <div className="info_item">
          <p className="title">{i18n.status}:</p>
          <div className="description">
            <Badge color={getTicketStatusBadgeType(props.detail?.ticketDetail.ticketStatusKey)}>
              {props.detail?.ticketDetail.ticketStatusName}
            </Badge>
          </div>
        </div>
        <div className="info_item">
          <p className="title">{i18n.ticket}:</p>
          <div className="description">#{props.detail?.ticketDetail.number}</div>
        </div>
        <div className="info_item">
          <p className="title">{i18n.created}:</p>
          <div className="description">{date.formatDateTime(props.detail?.ticketDetail.createdOnDate)}</div>
        </div>
        <div className="info_item">
          <p className="title">{i18n.assignedTo}:</p>
          <div className="description">
            {props.detail?.ticketDetail.agentFirstName
              ? `${props.detail?.ticketDetail.agentFirstName} ${props.detail?.ticketDetail.agentLastName}`
              : "-"}
          </div>
        </div>
      </div>

      <ModuleConfirmModal
        {...getRootContainerProps(props)}
        onClose={() => setShowConfirmation(false)}
        onConfirm={() => {
          props.onCloseTicket();
          setShowConfirmation(false);
        }}
        isVisible={showConfirmation}
      >
        <div>{i18n.infoCloseTicketConfirmation}</div>
      </ModuleConfirmModal>
    </div>
  );
};
